import './OrganizationUsersTable.scss'
import {get, map} from 'lodash'
import {Alert} from '@mui/material';
import {useContext, useState, useEffect} from "react";
import {DataGridWrapper} from "../../../Utils/DataGridWrapper/DataGridWrapper";
import {getOrganizationUsersApi} from "../../../Services/api";
import {EditUserCell} from "../EditUserCell/EditUserCell";
import {UserWelcomeMessageCell} from "../WelcomeMessageCell/UserWelcomeMessageCell";
import {UserDetailsCell} from "../../Settings/DeletedUsers/UserDetailsCell/UserDetailsCell";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {GridReactContextProvider, GridReactContext} from "../../../Utils/DataGridWrapper/GridContext";
import {PhoneCell} from "../../../Utils/PhoneCell/PhoneCell";
import {NO_ORGANIZATION_FILTER_VALUE} from "../../../Utils/Constants";

const getUsersColumns = ({i18n}) => {

    return [
        {
            field: 'firstName',
            headerName: i18n.translate("allUsersTable.columns.fullName"),
            minWidth: 160,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}}>{`${params.value} ${params.row.lastName}`}</div>,
        },
        {
            field: 'phoneFormatted',
            headerName: i18n.translate("allUsersTable.columns.phone"),
            minWidth: 150,
            align: 'right',
            sortable: false,
            renderCell: (params) => <PhoneCell value={params.value} />,
        },
        {
            field: 'email',
            headerName: i18n.translate("allUsersTable.columns.email"),
            minWidth: 220,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value}</div>,
        },
        {
            field: 'idNumber',
            headerName: i18n.translate("allUsersTable.columns.idNumber"),
            minWidth: 150,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value || '-'}</div>,
        },
        {
            field: 'employeeId',
            headerName: i18n.translate("allUsersTable.columns.employeeId"),
            minWidth: 150,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value || '-'}</div>,
        },
        {
            field: 'edit',
            headerName: i18n.translate("allUsersTable.columns.edit"),
            minWidth: 130,
            align: 'right',
            sortable: false,
            renderCell: (params) => <EditUserCell user={params.row}/>,
        },
        {
            field: 'userDetails',
            headerName: i18n.translate("deletedUserTable.columns.userDetails"),
            minWidth: 130,
            align: 'right',
            sortable: false,
            renderCell: (params) => <UserDetailsCell user={params.row}/>,
        },
        {
            field: 'sendWelcomeMessage',
            headerName: i18n.translate("allUsersTable.columns.sendWelcomeMessage"),
            minWidth: 180,
            align: 'right',
            sortable: false,
            renderCell: (params) => <UserWelcomeMessageCell user={params.row}/>,
        }
    ];
}

const tableFilters = {
    showFilters: {
        search: true,
        organizations: true
    },
    filterValues: {},
    filterData: {}
}

const convertData = (users) => {
    return map(users, (user) => {
        return user
    })
}

const paginationModel = {
    page: 0,
    pageSize: 100,
}

function OrganizationUsersTableInner({organizationsOptions, tableButtons}) {
    const {i18n} = useContext(I18nReactContext);
    const gridContext = useContext(GridReactContext);
    const [orphanedCount, setOrphanedCount] = useState(0);

    // Inject a "No organization" entry (with the orphan count) right after the
    // "all" option, but only when the company has orphaned users. The backend
    // returns orphanedUsersCount > 0 only for company admins.
    const buildOrgOptions = (baseOptions) => {
        const options = Array.isArray(baseOptions) ? [...baseOptions] : [];
        if (orphanedCount > 0) {
            const entry = {
                value: NO_ORGANIZATION_FILTER_VALUE,
                text: `${i18n.translate('orphanedUsers.filterLabel')}`,
                isOrphanedOption: true
            };
            const allIndex = options.findIndex(o => o.value === 'all');
            options.splice(allIndex >= 0 ? allIndex + 1 : 0, 0, entry);
        }
        return options;
    };

    const [localTableFilters, setLocalTableFilters] = useState(() => {
        const firstValue = organizationsOptions.length > 50 ? get(organizationsOptions, '[1].value') : get(organizationsOptions, '[0].value') || 'all';
        // Ensure organizationsOptions is an array and has valid values
        const validOrganizationsOptions = Array.isArray(organizationsOptions) ? organizationsOptions : [];
        
        return {
            ...tableFilters,
            filterData: {
                ...tableFilters.filterData,
                organizations: validOrganizationsOptions
            },
            filterValues: {
                ...tableFilters.filterValues,
                organizationIds: [firstValue],
            }
        };
    })
    
    // Sync organizationsOptions when they change (e.g., after importing users with
    // new orgs) or when the orphan count arrives — re-injecting the "No organization" entry.
    useEffect(() => {
        setLocalTableFilters(prevFilters => ({
            ...prevFilters,
            filterData: {
                ...prevFilters.filterData,
                organizations: buildOrgOptions(organizationsOptions)
            }
        }));
    }, [organizationsOptions, orphanedCount]);

    const getColumns = async ({paginationModel, filterModel}) => {
        return getUsersColumns({i18n})
    }

    // GridContext is the single source of truth for the selection. Writing
    // selection changes straight to context (instead of a separate local
    // useState) lets an external clear — e.g. after deleting users — actually
    // stick, rather than being overwritten by a stale local copy.
    const onSelectionModelChange = ({newSelectionModel}) => {
        gridContext.setSelectionModelToContext(newSelectionModel);
    };

    const apiToGetData = async ({paginationModel, filterModel}) => {
        const res = await getOrganizationUsersApi({
            paginationModel,
            filterModel
        })
        setOrphanedCount(get(res, 'orphanedUsersCount', 0))
        return res
    }
    
    return <>
        {orphanedCount > 0 && (
            <Alert
                severity="warning"
                className="OrganizationUsersTable__orphan-alert"
                role="alert"
            >
                <strong>{orphanedCount}</strong> {i18n.translate('orphanedUsers.alertMessage')}
            </Alert>
        )}
        <DataGridWrapper
        data-testid="users-table"
        buttons={tableButtons}
        filterModel={localTableFilters}
        disableColumnFilter
        paginationModel={paginationModel}
        getColumns={getColumns}
        apiToGetData={apiToGetData}
        dataConverter={convertData}
        checkboxSelection
        selectionModel={gridContext.selectionModel}
        onSelectionModelChange={onSelectionModelChange}
        keepNonExistentRowsSelected
        isRowSelectable={(params) => {
            const roles = get(params, 'row.roles') || {};
            const isCompanyAdmin = get(params, 'row.isCompanyAdmin') || false;
            return !(roles.SuperAdmin || isCompanyAdmin);
        }}
        />
    </>
}

export default function OrganizationUsersTable({organizationsOptions, tableButtons}) {
    return <OrganizationUsersTableInner organizationsOptions={organizationsOptions} tableButtons={tableButtons} />
}
